import type { AdminVideo } from '@/mocks/adminVideos';
import { categoryLabels, statusLabels, sourceTypeLabels, sourceTypeIcons } from '@/mocks/adminVideos';

interface VideoHistoryModalProps {
  open: boolean;
  video: AdminVideo | null;
  onClose: () => void;
}

interface HistoryEntry {
  date: string;
  action: string;
  detail: string;
  icon: string;
  color: string;
}

function generateHistory(video: AdminVideo): HistoryEntry[] {
  const baseEntries: HistoryEntry[] = [];

  // Published / uploaded
  if (video.status === 'publicado') {
    // Find the publication date - use uploadedAt or find it
    const pubDate = new Date(video.uploadedAt);
    pubDate.setHours(pubDate.getHours() + 2);
    baseEntries.push({
      date: pubDate.toISOString(),
      action: 'Publicado',
      detail: 'El video fue publicado y está disponible públicamente.',
      icon: 'ri-check-double-line',
      color: 'text-emerald-500 bg-emerald-100',
    });
  }

  if (video.status === 'programado' && video.scheduledAt) {
    baseEntries.push({
      date: video.scheduledAt,
      action: 'Programado',
      detail: `Publicación automática programada para el ${new Date(video.scheduledAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}.`,
      icon: 'ri-time-line',
      color: 'text-amber-500 bg-amber-100',
    });
  }

  // Featured
  if (video.isFeatured) {
    const featDate = new Date(video.uploadedAt);
    featDate.setHours(featDate.getHours() + 4);
    baseEntries.push({
      date: featDate.toISOString(),
      action: 'Destacado',
      detail: 'Marcado como video destacado. Aparece primero en la sección de videos.',
      icon: 'ri-star-fill',
      color: 'text-amber-500 bg-amber-100',
    });
  }

  // View milestones
  const uploadedDate = new Date(video.uploadedAt);
  const views = video.id * 37 + 120;

  if (views > 100) {
    const d100 = new Date(uploadedDate);
    d100.setDate(d100.getDate() + 1);
    baseEntries.push({
      date: d100.toISOString(),
      action: '100 visualizaciones',
      detail: 'El video alcanzó las 100 visualizaciones.',
      icon: 'ri-bar-chart-line',
      color: 'text-primary-500 bg-primary-100',
    });
  }

  if (views > 500) {
    const d500 = new Date(uploadedDate);
    d500.setDate(d500.getDate() + 3);
    baseEntries.push({
      date: d500.toISOString(),
      action: '500 visualizaciones',
      detail: 'El video alcanzó las 500 visualizaciones.',
      icon: 'ri-bar-chart-fill',
      color: 'text-accent-500 bg-accent-100',
    });
  }

  // Upload
  baseEntries.push({
    date: video.uploadedAt,
    action: 'Subido',
    detail: `Video subido desde ${sourceTypeLabels[video.sourceType]}. Tamaño: ${video.fileSize}.`,
    icon: 'ri-upload-cloud-2-line',
    color: 'text-foreground-500 bg-background-200',
  });

  // Sort by date descending
  baseEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return baseEntries;
}

export default function VideoHistoryModal({ open, video, onClose }: VideoHistoryModalProps) {
  if (!open || !video) return null;

  const views = video.id * 37 + 120;
  const lastViewedDate = new Date();
  lastViewedDate.setHours(lastViewedDate.getHours() - (video.id * 3));
  const uniqueViewers = Math.floor(views * 0.72);
  const avgWatchTime = `${Math.floor(video.id * 2.3 + 15)}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`;
  const history = generateHistory(video);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[8vh] overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-bold text-foreground-950">Historial del video</h3>
            <p className="text-[11px] text-foreground-400 mt-0.5 line-clamp-1">{video.title}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {/* Video info header */}
          <div className="p-5 border-b border-background-100">
            <div className="flex gap-4">
              <div className="w-28 h-16 rounded-xl overflow-hidden bg-background-200 shrink-0">
                <img
                  src={video.thumbnailUrl}
                  alt={video.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 space-y-1">
                <h4 className="text-sm font-bold text-foreground-950 line-clamp-2">{video.title}</h4>
                <div className="flex items-center flex-wrap gap-1.5">
                  <span className="px-1.5 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[9px] font-semibold">
                    {categoryLabels[video.category]}
                  </span>
                  <span className="px-1.5 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[9px] font-semibold flex items-center gap-1">
                    <i className={`${sourceTypeIcons[video.sourceType]} text-[9px]`}></i>
                    {sourceTypeLabels[video.sourceType]}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${
                    video.status === 'publicado'
                      ? 'bg-emerald-100 text-emerald-700'
                      : video.status === 'programado'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-foreground-200 text-foreground-600'
                  }`}>
                    {statusLabels[video.status]}
                  </span>
                </div>
                <p className="text-[10px] text-foreground-400">{video.duration} &bull; {video.fileSize}</p>
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-5 border-b border-background-100">
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-foreground-950">{views.toLocaleString()}</p>
              <p className="text-[10px] text-foreground-400 mt-0.5">Visualizaciones</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-foreground-950">{uniqueViewers.toLocaleString()}</p>
              <p className="text-[10px] text-foreground-400 mt-0.5">Espectadores únicos</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-foreground-950">{avgWatchTime}</p>
              <p className="text-[10px] text-foreground-400 mt-0.5">Promedio visto</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-foreground-950">
                {new Date(lastViewedDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
              </p>
              <p className="text-[10px] text-foreground-400 mt-0.5">Última visualización</p>
            </div>
          </div>

          {/* Platform breakdown */}
          <div className="p-5 border-b border-background-100">
            <h5 className="text-xs font-bold text-foreground-800 mb-3 flex items-center gap-1.5">
              <i className="ri-pie-chart-line text-sm text-foreground-500"></i>
              Origen del tráfico
            </h5>
            <div className="space-y-2.5">
              {[
                { label: 'Web (RadioFrecuenciaWeb)', pct: 45, color: 'bg-primary-500' },
                { label: 'YouTube', pct: video.sourceType === 'youtube' ? 38 : 25, color: 'bg-red-500' },
                { label: 'Facebook', pct: video.sourceType === 'facebook' ? 35 : 18, color: 'bg-blue-500' },
                { label: 'App Móvil', pct: 12, color: 'bg-emerald-500' },
              ].map((src) => (
                <div key={src.label} className="flex items-center gap-2.5">
                  <span className="text-[11px] text-foreground-600 w-40 truncate">{src.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-background-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${src.color} transition-all duration-500`}
                      style={{ width: `${src.pct}%` }}
                    ></div>
                  </div>
                  <span className="text-[11px] font-semibold text-foreground-700 w-10 text-right">{src.pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="p-5">
            <h5 className="text-xs font-bold text-foreground-800 mb-3 flex items-center gap-1.5">
              <i className="ri-history-line text-sm text-foreground-500"></i>
              Línea de tiempo
            </h5>

            <div className="relative pl-6 border-l-2 border-background-200 space-y-4 ml-2">
              {history.map((entry, i) => (
                <div key={i} className="relative">
                  <div className={`absolute -left-[31px] w-5 h-5 rounded-full flex items-center justify-center ${entry.color}`}>
                    <i className={`${entry.icon} text-[10px]`}></i>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-foreground-800">{entry.action}</span>
                      <span className="text-[9px] text-foreground-400">
                        {new Date(entry.date).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-500 leading-relaxed">{entry.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-4 border-t border-background-200 bg-background-100/70">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}